import React from 'react';
import { UseFormRegister, FieldErrors, Control, Controller } from 'react-hook-form';
import Select from '../../../components/Form/Select';
import AsyncSelect from '../../../components/Form/AsyncSelect';
import TextArea from '../../../components/Form/TextArea';
import { categoriesService } from '../../../services';
import { OutTransactionFormData, DropdownOption } from './outTransactionTypes';

interface OutRecipientSectionProps {
  register: UseFormRegister<OutTransactionFormData>;
  control: Control<OutTransactionFormData>;
  errors: FieldErrors<OutTransactionFormData>;
  branchOptions: DropdownOption[];
  employeeOptions: DropdownOption[];
  providerOptions: DropdownOption[];
  billOptions: DropdownOption[];
  recipientType: 'employee' | 'supplier';
  isMoneyIn: boolean;
  branchesLoading: boolean;
  employeesLoading: boolean;
  providersLoading: boolean;
  billsLoading: boolean;
}

// Server-side search + pagination for the category dropdown
const fetchCategories = async (query: string, page: number) => {
  return categoriesService.getAll({
    filters: query ? { name: { $containsi: query } } : undefined,
    pagination: { page, pageSize: 20 },
    sort: ['name:asc']
  }) as any;
};

const recipientTypeOptions = [
  { value: 'employee', label: 'Employee' },
  { value: 'supplier', label: 'Supplier' }
];

const OutRecipientSection: React.FC<OutRecipientSectionProps> = ({
  register,
  control,
  errors,
  branchOptions,
  employeeOptions,
  providerOptions,
  billOptions,
  recipientType,
  isMoneyIn,
  branchesLoading,
  employeesLoading,
  providersLoading,
  billsLoading,
}) => {
  if (isMoneyIn) return null;

  return (
    <>
      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Transaction Details
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Controller
            name="categoryId"
            control={control}
            rules={{ required: !isMoneyIn ? 'Category is required' : false }}
            render={({ field }) => (
              <AsyncSelect
                label="Category / Class"
                name="categoryId"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.categoryId?.message}
                required
                placeholder="Select a category..."
                fetchOptions={fetchCategories}
                getOptionLabel={(cat) => `${cat.name || ''}${cat.type ? ` (${cat.type})` : ''}`}
                getOptionValue={(cat) => cat.documentId || cat.id?.toString()}
              />
            )}
          />

          <Select
            label="Vessel / Headquarters"
            options={branchOptions}
            placeholder={branchesLoading ? "Loading branches..." : "Select vessel or headquarters"}
            required
            {...register('branchId', { required: 'Vessel/Headquarters is required' })}
            error={errors.branchId?.message}
          />
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Recipient Information
        </h3>

        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Recipient Type
            <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="flex space-x-6">
            {recipientTypeOptions.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="radio"
                  id={`recipientType-${option.value}`}
                  value={option.value}
                  {...register('recipientType')}
                  className="h-5 w-5 text-ocean-600 focus:ring-ocean-500 border-gray-300"
                />
                <label
                  htmlFor={`recipientType-${option.value}`}
                  className="ml-3 text-sm font-medium text-gray-700 cursor-pointer"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
          {errors.recipientType && (
            <p className="text-sm text-red-600 mt-2">{errors.recipientType.message}</p>
          )}
        </div>

        {recipientType === 'employee' && (
          <Select
            label="Employee"
            options={employeeOptions}
            placeholder={employeesLoading ? "Loading employees..." : "Select an employee"}
            required
            {...register('employeeId')}
            error={errors.employeeId?.message}
          />
        )}

        {recipientType === 'supplier' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Select
              label="Supplier"
              options={providerOptions}
              placeholder={providersLoading ? "Loading suppliers..." : "Select a supplier"}
              required
              {...register('supplierId')}
              error={errors.supplierId?.message}
            />

            <Select
              label="Related Bill"
              options={billOptions}
              placeholder={billsLoading ? "Loading bills..." : "Select a bill"}
              required
              {...register('billId')}
              error={errors.billId?.message}
            />
          </div>
        )}
      </div>

      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Additional Notes
        </h3>
        <TextArea
          label="Notes"
          {...register('notes')}
          error={errors.notes?.message}
          placeholder="Enter any additional notes or comments about this transaction"
          rows={4}
        />
      </div>
    </>
  );
};

export default OutRecipientSection;
